/**
 * Tooltip — a short label for a control whose purpose is not obvious from its face.
 *
 * WHY THIS EXISTS, and why it is not just `title`. Every IconButton already renders
 * `title={label}`, which is the browser's own tooltip, and that got the Studio most
 * of the way there. What it cannot do is any of the following: it takes roughly a
 * second to appear, it never appears on keyboard focus, it never appears on touch at
 * all, it is unstyleable, and it is announced by some screen readers in addition to
 * the accessible name — so on the surfaces the brief lists (volume, fullscreen, the
 * AI tools, the transport) it was a tooltip that only mouse users ever saw.
 *
 * WHAT IT IS NOT. It is not a way to give a control its name. `aria-label` (or visible
 * text) still does that, on every control, whether or not it is wrapped in one of
 * these — a tooltip that is the only label is a label that assistive tech may never
 * read, and that touch users certainly never see. So the bubble is `aria-hidden` by
 * default and adds nothing to the accessibility tree; it is a visual affordance over
 * a name that already exists. Pass `describe` for the minority case where the tooltip
 * carries information the name genuinely does not, and it is wired up with
 * `aria-describedby` instead. See the prop's comment.
 *
 * PORTALLED, and positioned in viewport coordinates. Both places the Studio needs
 * tooltips are inside clipping containers — the tool rail scrolls, the transport bar
 * is `overflow: hidden` and sits on the bottom edge — so an absolutely-positioned
 * bubble would be cut off by its own parent, and one that escaped the clip would
 * widen the page instead. Measuring the trigger and rendering to document.body avoids
 * both, and lets the bubble be clamped into the viewport rather than hanging off it.
 *
 * THREE WAYS IN, which is the actual requirement:
 *   mouse    hover, after a short delay so a cursor crossing the rail does not
 *            trail a line of bubbles behind it
 *   keyboard focus-visible, immediately — a keyboard user has already committed to
 *            the control, so there is nothing to debounce
 *   touch    a tap shows it and it fades on its own, because a touch device has no
 *            hover to leave and nothing to leave it with. The tap still activates
 *            the control: pointerdown does not cancel the click that follows, so
 *            the bubble confirms what was pressed rather than replacing it.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { c } from '../../styles/theme';

export type TooltipPlacement = 'top' | 'bottom';

export interface TooltipProps {
  /** The bubble's text. Kept to a few words — this is a label, not documentation. */
  label: string;
  /**
   * The control. Exactly one element, and it keeps its own accessible name: this
   * component wraps rather than clones, so nothing about the child changes.
   */
  children: ReactElement;
  /**
   * Which side the bubble prefers. It flips to the other side when there is no room,
   * so this is a preference and not a guarantee.
   */
  placement?: TooltipPlacement;
  /**
   * Wires the bubble up as the control's description.
   *
   * OFF by default, deliberately. Most of these tooltips restate the control's own
   * accessible name — "Open full-screen player" on a button already labelled that —
   * and describing it would have a screen reader read the same words twice. Turn it
   * on only when the bubble says something the name does not, which is how the AI
   * tools' cost tooltip uses it.
   */
  describe?: boolean;
  /**
   * Suppresses the bubble entirely, while keeping the wrapper in place.
   *
   * For a trigger that opens something of its own — the "More" menu is the case this
   * was added for. A tooltip explaining the button would otherwise sit on top of the
   * menu the button just opened, and on touch it would sit there for the full
   * TOUCH_DURATION_MS covering the items the user is trying to read.
   *
   * The wrapper still renders rather than falling back to a bare child, so toggling
   * this does not change the tree shape and does not remount the control — which for
   * a menu trigger would drop the focus that Escape has to return to.
   */
  disabled?: boolean;
  /** Applied to the inline-flex wrapper, for layout only. */
  style?: CSSProperties;
  /**
   * A class on the inline-flex wrapper, for layout that has to change at a breakpoint.
   *
   * The alternative to `style` rather than a companion to it, and the distinction is the
   * one the whole stylesheet is organised around: an inline style prop outranks every
   * media query, so a width passed through `style` cannot be undone on a phone. The tool
   * rail's rows were exactly that — `style={{ width: '100%' }}` on eleven wrappers, which
   * the 900px block's `.lyx-ws-tool { width: auto }` had no way to reach, so each row went
   * on demanding a full rail width inside the horizontal scroller and the palette showed
   * one tool per swipe.
   *
   * Use this when the wrapper's size depends on the viewport; `style` is still right for a
   * fixed layout nudge. See .lyx-ws-tool-wrap.
   */
  className?: string;
}

/** Hover delay. Long enough not to fire in passing, short enough not to feel broken. */
const SHOW_DELAY_MS = 380;

/** How long a touch-triggered bubble stays. There is no pointer-leave to end it. */
const TOUCH_DURATION_MS = 2400;

/** Gap between the trigger and the bubble, and the minimum clearance from the edge. */
const OFFSET = 8;
const EDGE = 8;

/** Where the bubble is drawn, in viewport coordinates. */
interface Position {
  top: number;
  left: number;
  /** The side it actually ended up on, which may not be the one asked for. */
  placement: TooltipPlacement;
}

export default function Tooltip({
  label,
  children,
  placement = 'top',
  describe = false,
  disabled = false,
  style,
  className,
}: TooltipProps) {
  const id = useId();
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * True between a pointer press and the focus event it causes.
   *
   * Pressing a button focuses it, so `onFocus` fires after every click and tap — and
   * without this flag it undid both of the pointer paths below. A mouse click hid the
   * bubble on pointerdown and the focus immediately put it back, so the bubble sat over
   * the panel the click had just opened; a tap showed the bubble and armed the
   * auto-dismiss, then the focus called `clearTimers` and the bubble stayed up until
   * something else took focus away.
   *
   * The browser's own answer to this is `:focus-visible`, which does not match after a
   * mouse press. It is not used here because it does not distinguish the touch case —
   * and because jsdom's support for the selector cannot be relied on, which would make
   * the behaviour untestable. So the flag: set on press, consumed by the focus that
   * follows, and cleared on the way out in case no focus ever arrives.
   */
  const fromPointer = useRef(false);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<Position | null>(null);

  const clearTimers = useCallback(() => {
    if (showTimer.current) clearTimeout(showTimer.current);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    showTimer.current = null;
    hideTimer.current = null;
  }, []);

  const hide = useCallback(() => {
    clearTimers();
    setOpen(false);
  }, [clearTimers]);

  // Timers outlive the component if the trigger unmounts while one is pending —
  // which the tool rail does every time a panel opens.
  useEffect(() => clearTimers, [clearTimers]);

  /**
   * Measures the trigger and places the bubble, flipping and clamping to fit.
   *
   * Run after the bubble is in the DOM, because its own width decides the clamp. In
   * jsdom every rect is zero, so this resolves to the top-left corner; the position
   * is a browser concern and the tests assert the bubble's presence and text.
   */
  const place = useCallback(() => {
    const trigger = wrapRef.current;
    const bubble = bubbleRef.current;
    if (!trigger) return;

    const t = trigger.getBoundingClientRect();
    const b = bubble?.getBoundingClientRect();
    const width = b?.width ?? 0;
    const height = b?.height ?? 0;

    // Flip when the preferred side has no room. Checked against the bubble's real
    // height rather than a guess, so a two-line bubble flips when a one-line one
    // would not have needed to.
    let side = placement;
    if (side === 'top' && t.top - height - OFFSET < EDGE) side = 'bottom';
    else if (side === 'bottom' && t.bottom + height + OFFSET > window.innerHeight - EDGE)
      side = 'top';

    const top = side === 'top' ? t.top - height - OFFSET : t.bottom + OFFSET;

    // Centred on the trigger, then pulled back inside the viewport. The clamp is why
    // a tooltip on the leftmost rail row or the rightmost transport button does not
    // hang off the edge — and Math.max last, so on a viewport narrower than the
    // bubble it sits at the left edge rather than being pushed off the other side.
    const centred = t.left + t.width / 2 - width / 2;
    const left = Math.max(EDGE, Math.min(centred, window.innerWidth - width - EDGE));

    setPos({ top, left, placement: side });
  }, [placement]);

  /*
    A LAYOUT effect, not a plain one: it has to run before the browser paints, or the
    bubble is briefly drawn at the top-left corner and then jumps to the trigger.
    `opacity: 0` until measured covers the same ground defensively, but the two
    together are what make the appearance clean — the fade should be the only motion.
  */
  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [open, place]);

  /*
    Anything that moves the trigger dismisses the bubble rather than chasing it.

    The bubble is positioned in viewport coordinates, so a scroll leaves it behind —
    and the containers these sit in scroll (the tool rail, the workspace body).
    Recomputing on every scroll frame would be the alternative; dismissing is both
    cheaper and what a native tooltip does.

    Capture phase, because the scroll happens on an inner container and scroll events
    do not bubble to window from elements.
  */
  useEffect(() => {
    if (!open) return;
    const onMove = () => hide();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, hide]);

  // Covers the case the guards in showNow/showAfterDelay cannot: a bubble already up
  // when `disabled` flips true, which is exactly what happens when the pointer is
  // resting on the "More" trigger at the moment its menu opens.
  useEffect(() => {
    if (disabled) hide();
  }, [disabled, hide]);

  function showAfterDelay() {
    if (disabled) return;
    clearTimers();
    showTimer.current = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
  }

  function showNow() {
    if (disabled) return;
    clearTimers();
    setOpen(true);
  }

  return (
    <span
      ref={wrapRef}
      className={className}
      style={{ display: 'inline-flex', ...style }}
      onPointerEnter={(e) => {
        // Mouse only. A touch produces pointerenter as well, and honouring it here
        // would show the bubble on tap *and* start the touch timer below.
        if (e.pointerType === 'mouse') showAfterDelay();
      }}
      onPointerLeave={(e) => {
        /*
          Mouse only, and this is not a nicety. A touch pointer stops existing when the
          finger lifts, so a tap fires pointerleave *before* the click and the focus that
          follows it — meaning a leave handled here would clear the dismiss timer just
          armed below and release the guard, and the focus arriving a moment later would
          reopen a bubble with nothing left to close it. There is also nothing to leave:
          a touch has no hover state, which is why the timer owns the dismissal.
        */
        if (e.pointerType !== 'mouse') return;
        fromPointer.current = false;
        hide();
      }}
      onPointerDown={(e) => {
        // Claimed by the focus event this press is about to cause, so that focus does
        // not reopen (mouse) or cancel the dismissal of (touch) what happens here.
        fromPointer.current = true;
        if (e.pointerType === 'mouse') {
          // Pressing the control means the user knows what it does. Keeping the
          // bubble up would then cover the thing they were about to look at.
          hide();
          return;
        }
        showNow();
        hideTimer.current = setTimeout(() => setOpen(false), TOUCH_DURATION_MS);
      }}
      /*
        focusin/focusout rather than focus/blur: the focusable element is the child,
        not this wrapper, and focus does not bubble. React's onFocus is synthesised
        from focusin so it would work too — these are named for what they are.
      */
      onFocus={() => {
        // The focus that follows a click or a tap is not a keyboard arrival, and the
        // pointer handler has already decided what the bubble should do. See
        // `fromPointer`.
        if (fromPointer.current) {
          fromPointer.current = false;
          return;
        }
        showNow();
      }}
      onBlur={() => {
        fromPointer.current = false;
        hide();
      }}
      onKeyDown={(e) => {
        // Escape dismisses a tooltip without moving focus — WCAG 1.4.13's
        // "dismissable" requirement, and the only way to uncover content the bubble
        // is sitting on top of while keeping the control focused.
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          hide();
        }
      }}
      aria-describedby={describe && open ? id : undefined}
    >
      {children}

      {open &&
        createPortal(
          <div
            ref={bubbleRef}
            id={id}
            // Described tooltips are part of the accessibility tree; the default kind
            // is decoration over a name that is already there. See `describe`.
            {...(describe ? { role: 'tooltip' } : { 'aria-hidden': true })}
            className="lyx-tooltip"
            data-placement={pos?.placement ?? placement}
            style={{
              position: 'fixed',
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              // Hidden until measured, so it never flashes at the corner first.
              opacity: pos ? 1 : 0,
              // Nothing about a tooltip is interactive, and a bubble that could be
              // hovered would sit between the cursor and the control underneath it.
              pointerEvents: 'none',
              background: c.surface3,
              border: `1px solid ${c.borderStrong}`,
              borderRadius: 7,
              padding: '5px 9px',
              // Portalled onto document.body, which is outside AppShell's root div —
              // so neither the colour nor the font is inherited and both are stated
              // here. text1 on surface3 is 12.4:1. See Toast for the same reason.
              color: c.text1,
              fontFamily: 'Inter, sans-serif',
              fontSize: 11.5,
              lineHeight: 1.45,
              fontWeight: 500,
              whiteSpace: 'normal',
              // Wide enough for a short phrase, narrow enough to stay a tooltip.
              maxWidth: 'min(240px, calc(100vw - 16px))',
              boxShadow: '0 6px 20px rgba(0,0,0,0.45)',
            }}
          >
            {label}
          </div>,
          document.body
        )}
    </span>
  );
}
